import { useEffect, useState, useMemo, useRef } from 'react';
import { tasksApi } from '../api';
import { ChevronLeft, ChevronRight, Plus, AlertCircle, X } from 'lucide-react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/ru';
import TaskModal from '../components/TaskModal';
import { useAuth } from '../hooks/useAuth';

dayjs.extend(isoWeek);
dayjs.locale('ru');

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const priorityConfig: Record<string, { label: string; color: string; bg: string }> = {
  LOW:    { label: 'Низкий',   color: '#64748b', bg: '#f1f5f9' },
  MEDIUM: { label: 'Средний',  color: '#3b82f6', bg: '#dbeafe' },
  HIGH:   { label: 'Высокий',  color: '#f59e0b', bg: '#fef3c7' },
  URGENT: { label: 'Срочно',   color: '#ef4444', bg: '#fee2e2' },
};

const statusConfig: Record<string, { label: string; color: string }> = {
  PENDING:     { label: 'Ожидает',    color: '#f59e0b' },
  IN_PROGRESS: { label: 'В работе',   color: '#3b82f6' },
  COMPLETED:   { label: 'Выполнена',  color: '#10b981' },
  CANCELLED:   { label: 'Отменена',   color: '#94a3b8' },
};

function isOverdue(task: any): boolean {
  if (task.isCompleted || task.status === 'COMPLETED' || task.status === 'CANCELLED') return false;
  if (!task.dueDate) return false;
  return dayjs(task.dueDate).isBefore(dayjs(), 'day');
}

function sortByTime(tasks: any[]): any[] {
  return [...tasks].sort((a, b) => {
    if (a.dueTime && b.dueTime) return a.dueTime.localeCompare(b.dueTime);
    if (a.dueTime) return -1;
    if (b.dueTime) return 1;
    return 0;
  });
}

export default function CalendarPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [calendarFilter, setCalendarFilter] = useState<'all' | 'public' | 'mine'>('all');
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [defaultDate, setDefaultDate] = useState<string | undefined>(undefined);
  const [maxChips, setMaxChips] = useState(3);
  const [dayPopover, setDayPopover] = useState<{ date: string; rect: DOMRect } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const startOfMonth = currentDate.startOf('month');
  const endOfMonth = currentDate.endOf('month');
  const startOfCalendar = startOfMonth.startOf('isoWeek');
  const endOfCalendar = endOfMonth.endOf('isoWeek');

  const load = async () => {
    setLoading(true);
    try {
      const res = await tasksApi.list({
        dueDateFrom: startOfCalendar.format('YYYY-MM-DD'),
        dueDateTo: endOfCalendar.format('YYYY-MM-DD'),
        limit: 200,
      });
      setTasks(res.data.data || []);
    } catch (e) {
      console.error('Load calendar tasks error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [currentDate]);

  const filteredTasks = useMemo(() => {
    if (!isAdmin || calendarFilter === 'all') return tasks;
    if (calendarFilter === 'public') {
      return tasks.filter(t => (t.participants?.length > 0) || (t.participantOrganizations?.length > 0));
    }
    if (calendarFilter === 'mine') {
      const uid = user?.id;
      return tasks.filter(t =>
        t.assigneeId === uid ||
        t.participants?.some((p: any) => p.id === uid)
      );
    }
    return tasks;
  }, [tasks, calendarFilter, isAdmin, user?.id]);

  const tasksByDate = useMemo(() => {
    const map: Record<string, any[]> = {};
    filteredTasks.forEach(task => {
      if (!task.dueDate) return;
      const key = dayjs(task.dueDate).format('YYYY-MM-DD');
      if (!map[key]) map[key] = [];
      map[key].push(task);
    });
    return map;
  }, [filteredTasks]);

  const calendarDays = useMemo(() => {
    const days: dayjs.Dayjs[] = [];
    let day = startOfCalendar;
    while (day.isBefore(endOfCalendar) || day.isSame(endOfCalendar, 'day')) {
      days.push(day);
      day = day.add(1, 'day');
    }
    return days;
  }, [currentDate]);

  const weekRows = Math.ceil(calendarDays.length / 7);

  useEffect(() => {
    const calc = () => {
      const cellH = (window.innerHeight - 320) / weekRows;
      setMaxChips(Math.min(8, Math.max(2, Math.floor((cellH - 34) / 21))));
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [weekRows]);

  useEffect(() => {
    if (!dayPopover) return;
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setDayPopover(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDayPopover(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [dayPopover]);

  const goToPrev = () => { setDayPopover(null); setCurrentDate(d => d.subtract(1, 'month')); };
  const goToNext = () => { setDayPopover(null); setCurrentDate(d => d.add(1, 'month')); };
  const goToToday = () => { setDayPopover(null); setCurrentDate(dayjs()); };

  const openDayPopover = (date: string, el: HTMLElement) => {
    setDayPopover({ date, rect: el.getBoundingClientRect() });
  };

  const openEdit = (task: any) => {
    setDayPopover(null);
    setEditing(task);
    setDefaultDate(undefined);
    setShowModal(true);
  };

  const openCreate = (date: string) => {
    setDayPopover(null);
    setEditing(null);
    setDefaultDate(date);
    setShowModal(true);
  };

  const dayTasks = (key: string) => sortByTime(tasksByDate[key] || []);
  const popoverTasks = dayPopover ? dayTasks(dayPopover.date) : [];
  const popoverOpenUp = dayPopover
    ? window.innerHeight - dayPopover.rect.bottom < 340 && dayPopover.rect.top > 340
    : false;

  return (
    <div className="calendar-page">
      <div className="page-header">
        <h2 className="page-title">Календарь</h2>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {isAdmin && (
            <div className="btn-group">
              <button className={`btn btn-sm ${calendarFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setCalendarFilter('all')}>Все</button>
              <button className={`btn btn-sm ${calendarFilter === 'public' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setCalendarFilter('public')}>Публичные</button>
              <button className={`btn btn-sm ${calendarFilter === 'mine' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setCalendarFilter('mine')}>Мои</button>
            </div>
          )}
          <button className="btn btn-secondary btn-sm" onClick={goToToday}>Сегодня</button>
        </div>
      </div>

      <div className="calendar-layout">
        <div className="calendar-main">
          <div className="card">
            <div className="calendar-nav">
              <button className="calendar-nav-btn" onClick={goToPrev}><ChevronLeft size={20} /></button>
              <h3 className="calendar-nav-title">{currentDate.format('MMMM YYYY')}</h3>
              <button className="calendar-nav-btn" onClick={goToNext}><ChevronRight size={20} /></button>
            </div>

            <div className="calendar-grid">
              <div className="calendar-weekdays">
                {WEEKDAYS.map(d => (
                  <div key={d} className="calendar-weekday">{d}</div>
                ))}
              </div>

              <div className="calendar-days">
                {calendarDays.map((day, i) => {
                  const key = day.format('YYYY-MM-DD');
                  const isCurrentMonth = day.month() === currentDate.month();
                  const isToday = key === dayjs().format('YYYY-MM-DD');
                  const tasksForDay = tasksByDate[key] || [];
                  const hasUrgent = tasksForDay.some(t => t.priority === 'URGENT');

                  return (
                    <div
                      key={i}
                      className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${dayPopover?.date === key ? 'selected' : ''}`}
                      onClick={(e) => openDayPopover(key, e.currentTarget)}
                      onDoubleClick={() => openCreate(key)}
                    >
                      <div className="calendar-day-header">
                        <span className={`calendar-day-number ${isToday ? 'today-number' : ''}`}>
                          {day.date()}
                        </span>
                        <div className="calendar-day-actions">
                          {hasUrgent && <span className="calendar-urgent-dot" />}
                          <button
                            className="calendar-day-add-btn"
                            title="Добавить задачу на этот день"
                            onClick={(e) => { e.stopPropagation(); openCreate(key); }}
                            onDoubleClick={(e) => e.stopPropagation()}
                          >
                            <Plus size={13} />
                          </button>
                        </div>
                      </div>
                      <div className="calendar-day-tasks">
                        {sortByTime(tasksForDay).slice(0, maxChips).map(task => (
                          <div
                            key={task.id}
                            className={`calendar-task-chip ${task.isCompleted ? 'completed' : ''}`}
                            style={{ borderLeftColor: task.isCompleted ? '#10b981' : isOverdue(task) ? '#ef4444' : (priorityConfig[task.priority]?.color || '#94a3b8') }}
                            onClick={(e) => { e.stopPropagation(); openEdit(task); }}
                            onDoubleClick={(e) => e.stopPropagation()}
                            title={`${task.dueTime ? task.dueTime + ' ' : ''}${task.title}`}
                          >
                            {task.dueTime && <span className="calendar-task-chip-time">{task.dueTime}</span>}
                            <span className="calendar-task-chip-text">{task.title}</span>
                          </div>
                        ))}
                        {tasksForDay.length > maxChips && (
                          <button
                            className="calendar-more-tasks"
                            onClick={(e) => {
                              e.stopPropagation();
                              const cell = (e.currentTarget as HTMLElement).closest('.calendar-day');
                              if (cell) openDayPopover(key, cell as HTMLElement);
                            }}
                            onDoubleClick={(e) => e.stopPropagation()}
                          >
                            +{tasksForDay.length - maxChips} ещё
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {dayPopover && (
        <div
          ref={popoverRef}
          className="calendar-day-popover"
          style={{
            left: Math.min(Math.max(8, dayPopover.rect.left), window.innerWidth - 336),
            width: Math.max(dayPopover.rect.width, 320),
            ...(popoverOpenUp
              ? { bottom: window.innerHeight - dayPopover.rect.top + 8 }
              : { top: dayPopover.rect.bottom + 8 }),
          }}
        >
          <div className="calendar-day-popover-header">
            <h4>{dayjs(dayPopover.date).format('D MMMM, dddd')}</h4>
            <div className="flex gap-2" style={{ alignItems: 'center' }}>
              {popoverTasks.length > 0 && <span className="badge badge-info">{popoverTasks.length}</span>}
              <button className="header-icon-btn" style={{ width: 28, height: 28 }} onClick={() => setDayPopover(null)}>
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="calendar-day-popover-body">
            {loading ? (
              <div className="calendar-empty">Загрузка...</div>
            ) : popoverTasks.length === 0 ? (
              <div className="calendar-empty">
                <AlertCircle size={28} style={{ color: 'var(--color-text-muted)', marginBottom: 8 }} />
                <div style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>Нет задач на этот день</div>
              </div>
            ) : (
              <div className="calendar-task-list">
                {popoverTasks.map(task => (
                  <div
                    key={task.id}
                    className={`calendar-task-card ${task.isCompleted ? 'completed' : ''}`}
                    style={isOverdue(task) ? { borderLeft: '3px solid #ef4444' } : undefined}
                    onClick={() => openEdit(task)}
                  >
                    <div className="calendar-task-card-top">
                      <div className="calendar-task-time-badge" style={{ background: isOverdue(task) ? '#fee2e2' : priorityConfig[task.priority]?.bg, color: isOverdue(task) ? '#ef4444' : priorityConfig[task.priority]?.color }}>
                        {task.dueTime || '— : —'}
                      </div>
                      <span className="badge" style={{ fontSize: 11, background: task.isCompleted ? '#dcfce7' : isOverdue(task) ? '#fee2e2' : '#fef9c3', color: task.isCompleted ? '#166534' : isOverdue(task) ? '#991b1b' : '#854d0e' }}>
                        {task.isCompleted ? 'Выполнена' : isOverdue(task) ? 'Просрочена' : statusConfig[task.status]?.label || task.status}
                      </span>
                    </div>
                    <div className="calendar-task-card-title" style={{ textDecoration: task.isCompleted ? 'line-through' : 'none' }}>
                      {task.title}
                    </div>
                    {task.description && (
                      <div className="calendar-task-card-desc">{task.description}</div>
                    )}
                    <div className="calendar-task-card-footer">
                      <span className="calendar-task-assignee">
                        {task.assignee?.firstName} {task.assignee?.lastName}
                      </span>
                      <span className="calendar-task-priority" style={{ color: priorityConfig[task.priority]?.color }}>
                        {priorityConfig[task.priority]?.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="calendar-day-popover-footer">
            <button className="btn btn-primary btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => openCreate(dayPopover.date)}>
              <Plus size={15} /> Добавить задачу
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <TaskModal
          task={editing}
          defaultDueDate={defaultDate}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}
